import React, { useState, useCallback } from 'react';
import { Download, FileSpreadsheet, Calendar, Loader2 } from 'lucide-react';
import * as XLSX from 'xlsx';
import { API_URL } from '../config';

const ExportCongesHCR = ({ authToken, authUser }) => {
  const now = new Date();
  const [selectedMonth, setSelectedMonth] = useState(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [filterService, setFilterService] = useState('');
  const [filterEmployee, setFilterEmployee] = useState('');

  const heuresMensuelles = { 35: 151.67, 39: 169.00, 40: 173.33 };

  const loadPreview = useCallback(async () => {
    setLoading(true);
    try {
      const [year, month] = selectedMonth.split('-');
      const res = await fetch(`${API_URL}/export-paie-conges?year=${year}&month=${month}`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      if (!res.ok) throw new Error('Erreur chargement');
      setData(await res.json());
    } catch (err) {
      console.error('Erreur chargement données export:', err);
    } finally {
      setLoading(false);
    }
  }, [authToken, selectedMonth]);

  // Services uniques pour le filtre
  const services = data ? [...new Set(data.employes.map(e => e.employee.service).filter(Boolean))].sort() : [];

  // Données filtrées
  const filteredEmployes = data ? data.employes.filter(e => {
    if (filterService && e.employee.service !== filterService) return false;
    if (filterEmployee && e.employee.id !== parseInt(filterEmployee)) return false;
    return true;
  }) : [];

  const filteredDetail = data ? data.conges_detail.filter(c => {
    const emp = data.employes.find(e => e.employee.id === c.employee_id);
    if (filterService && emp && emp.employee.service !== filterService) return false;
    if (filterEmployee && c.employee_id !== parseInt(filterEmployee)) return false;
    return true;
  }) : [];

  const exportExcel = useCallback(() => {
    if (!data) return;
    setExporting(true);

    try {
      const [year, month] = selectedMonth.split('-');
      const exportList = filteredEmployes;
      const exportDetail = filteredDetail;

      // Feuille 1 — Résumé Mensuel Paie
      const resumeData = exportList.map(e => ({
        'Employé': e.employee.nom,
        'Service': e.employee.service,
        'Rôle': e.employee.role,
        'Contrat (h/sem)': e.employee.heures_hebdomadaires,
        'Heures Contrat Mois': e.contrat.heures_mensuelles,
        'Jours Pointés': e.pointages.jours_pointes,
        'Heures Pointées': e.pointages.heures_pointees,
        'Jours CP Pris (mois)': e.conges_mois.jours_ouvrables_cp,
        'Jours Autres Absences': e.conges_mois.jours_autres,
        'Heures CP (mois)': e.conges_mois.heures_equivalentes,
        'Total Heures (Pointé + CP)': e.total_heures,
        'Écart vs Contrat': e.ecart_contrat,
        'Solde CP Acquis': e.solde_cp.acquis,
        'Solde CP Pris': e.solde_cp.pris,
        'Solde CP Restant': e.solde_cp.restant,
        'Compteur Modulation': e.compteur_modulation
      }));

      // Feuille 2 — Détail Congés
      const detailData = exportDetail.map(c => {
        const emp = data.employes.find(e => e.employee.id === c.employee_id);
        const heuresJour = emp ? (({ 35: 7, 39: 7.8, 40: 8 })[emp.employee.heures_hebdomadaires] || 7) : 7;
        return {
          'Employé': c.employee_nom,
          'Type': c.type === 'conges_payes' ? 'Congés payés' :
                  c.type === 'rtt' ? 'RTT' :
                  c.type === 'maladie' ? 'Maladie' :
                  c.type === 'conges_sans_solde' ? 'Sans solde' : c.type,
          'Date Début': c.date_debut,
          'Date Fin': c.date_fin,
          'Jours Ouvrables': c.jours_ouvrables,
          'Heures Équivalentes': parseFloat((c.jours_ouvrables * heuresJour).toFixed(2))
        };
      });

      const wb = XLSX.utils.book_new();

      // Feuille 1
      const ws1 = XLSX.utils.json_to_sheet(resumeData);
      // Largeur des colonnes
      ws1['!cols'] = [
        { wch: 25 }, { wch: 22 }, { wch: 15 }, { wch: 14 }, { wch: 18 },
        { wch: 13 }, { wch: 15 }, { wch: 18 }, { wch: 20 }, { wch: 16 },
        { wch: 22 }, { wch: 16 }, { wch: 14 }, { wch: 13 }, { wch: 16 }, { wch: 18 }
      ];
      XLSX.utils.book_append_sheet(wb, ws1, 'Résumé Mensuel Paie');

      // Feuille 2
      if (detailData.length > 0) {
        const ws2 = XLSX.utils.json_to_sheet(detailData);
        ws2['!cols'] = [
          { wch: 25 }, { wch: 15 }, { wch: 12 }, { wch: 12 }, { wch: 16 }, { wch: 18 }
        ];
        XLSX.utils.book_append_sheet(wb, ws2, 'Détail Congés');
      }

      const suffix = filterEmployee ? `_${exportList[0]?.employee.nom.replace(/\s/g, '_')}` : filterService ? `_${filterService.replace(/\s/g, '_')}` : '';
      XLSX.writeFile(wb, `paie_hcr_conges_${year}_${month}${suffix}.xlsx`);
    } catch (err) {
      console.error('Erreur export Excel:', err);
    } finally {
      setExporting(false);
    }
  }, [data, selectedMonth, filteredEmployes, filteredDetail, filterEmployee, filterService]);

  const getEcartColor = (ecart) => {
    if (ecart > 5) return 'text-green-600 dark:text-green-400';
    if (ecart < -5) return 'text-red-600 dark:text-red-400';
    return 'text-text-secondary';
  };

  return (
    <div className="bg-surface rounded-xl shadow-lg p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="bg-emerald-100 dark:bg-emerald-900/40 p-3 rounded-xl">
          <FileSpreadsheet className="w-6 h-6 text-emerald-600 dark:text-emerald-400" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-text-primary">Export Paie HCR — Heures & Congés</h2>
          <p className="text-sm text-text-muted">Export mensuel combinant pointages, congés et contrat</p>
        </div>
      </div>

      {/* Sélection du mois + filtres */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-text-muted" />
          <input
            type="month"
            value={selectedMonth}
            onChange={e => { setSelectedMonth(e.target.value); setData(null); }}
            className="px-3 py-2 border border-border-subtle rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <button
          onClick={loadPreview}
          disabled={loading}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm font-medium transition flex items-center gap-2"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
          Charger les données
        </button>
        {data && (
          <button
            onClick={exportExcel}
            disabled={exporting || filteredEmployes.length === 0}
            className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 text-sm font-medium transition flex items-center gap-2"
          >
            <Download className="w-4 h-4" />
            {exporting ? 'Export en cours...' : `Exporter Excel (${filteredEmployes.length})`}
          </button>
        )}
      </div>

      {/* Filtres service + employé */}
      {data && (
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <select
            value={filterService}
            onChange={e => { setFilterService(e.target.value); setFilterEmployee(''); }}
            className="px-3 py-2 border border-border-subtle rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Tous les services</option>
            {services.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <select
            value={filterEmployee}
            onChange={e => setFilterEmployee(e.target.value)}
            className="px-3 py-2 border border-border-subtle rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Tous les employés</option>
            {data.employes
              .filter(e => !filterService || e.employee.service === filterService)
              .map(e => <option key={e.employee.id} value={e.employee.id}>{e.employee.nom}</option>)
            }
          </select>
          {(filterService || filterEmployee) && (
            <button
              onClick={() => { setFilterService(''); setFilterEmployee(''); }}
              className="px-3 py-2 text-text-muted hover:text-text-secondary text-sm transition"
            >
              Réinitialiser
            </button>
          )}
        </div>
      )}

      {/* Prévisualisation */}
      {data && (
        <>
          {/* Résumé rapide */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <div className="bg-blue-50 dark:bg-blue-950/40 rounded-lg p-3 text-center">
              <p className="text-lg font-bold text-blue-700">{filteredEmployes.length}</p>
              <p className="text-xs text-blue-600 dark:text-blue-400">Employés</p>
            </div>
            <div className="bg-green-50 dark:bg-green-950/40 rounded-lg p-3 text-center">
              <p className="text-lg font-bold text-green-700">
                {filteredEmployes.reduce((s, e) => s + e.pointages.heures_pointees, 0).toFixed(1)}h
              </p>
              <p className="text-xs text-green-600 dark:text-green-400">Heures pointées</p>
            </div>
            <div className="bg-amber-50 dark:bg-amber-950/40 rounded-lg p-3 text-center">
              <p className="text-lg font-bold text-amber-700">
                {filteredEmployes.reduce((s, e) => s + e.conges_mois.jours_ouvrables_cp, 0)}
              </p>
              <p className="text-xs text-amber-600 dark:text-amber-400">Jours CP pris</p>
            </div>
            <div className="bg-purple-50 dark:bg-purple-950/40 rounded-lg p-3 text-center">
              <p className="text-lg font-bold text-purple-700">
                {filteredDetail.length}
              </p>
              <p className="text-xs text-purple-600 dark:text-purple-400">Demandes congés</p>
            </div>
          </div>

          {/* Tableau prévisualisation */}
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-border-subtle bg-elevated-1">
                  <th className="text-left py-2 px-2">Employé</th>
                  <th className="text-left py-2 px-2">Service</th>
                  <th className="text-center py-2 px-2">Contrat</th>
                  <th className="text-center py-2 px-2">H. Contrat</th>
                  <th className="text-center py-2 px-2">J. Pointés</th>
                  <th className="text-center py-2 px-2">H. Pointées</th>
                  <th className="text-center py-2 px-2">J. CP</th>
                  <th className="text-center py-2 px-2">H. CP</th>
                  <th className="text-center py-2 px-2">Total H.</th>
                  <th className="text-center py-2 px-2">Écart</th>
                  <th className="text-center py-2 px-2">Solde CP</th>
                  <th className="text-center py-2 px-2">Modul.</th>
                </tr>
              </thead>
              <tbody>
                {filteredEmployes.map(e => (
                  <tr key={e.employee.id} className="border-b border-border-subtle hover:bg-elevated-1">
                    <td className="py-2 px-2 font-medium">{e.employee.nom}</td>
                    <td className="py-2 px-2 text-text-secondary">{e.employee.service}</td>
                    <td className="py-2 px-2 text-center">{e.employee.heures_hebdomadaires}h</td>
                    <td className="py-2 px-2 text-center">{e.contrat.heures_mensuelles}h</td>
                    <td className="py-2 px-2 text-center">{e.pointages.jours_pointes}</td>
                    <td className="py-2 px-2 text-center">{e.pointages.heures_pointees}h</td>
                    <td className="py-2 px-2 text-center">{e.conges_mois.jours_ouvrables_cp || '—'}</td>
                    <td className="py-2 px-2 text-center">{e.conges_mois.heures_equivalentes || '—'}</td>
                    <td className="py-2 px-2 text-center font-medium">{e.total_heures}h</td>
                    <td className={`py-2 px-2 text-center font-medium ${getEcartColor(e.ecart_contrat)}`}>
                      {e.ecart_contrat > 0 ? '+' : ''}{e.ecart_contrat}h
                    </td>
                    <td className="py-2 px-2 text-center">
                      <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${
                        e.solde_cp.restant < 5 ? 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-300' :
                        e.solde_cp.restant < 10 ? 'bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300' :
                        'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-300'
                      }`}>{e.solde_cp.restant}j</span>
                    </td>
                    <td className="py-2 px-2 text-center text-text-secondary">{e.compteur_modulation}h</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="text-xs text-text-muted mt-4 text-center">
            Mois : {data.mois} — Convention HCR — Jours ouvrables (lundi-samedi)
          </p>
        </>
      )}

      {!data && !loading && (
        <div className="text-center py-12 text-text-muted">
          <FileSpreadsheet className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p>Sélectionnez un mois et cliquez sur "Charger les données" pour prévisualiser</p>
        </div>
      )}
    </div>
  );
};

export default ExportCongesHCR;
